import { ChevronDown } from "lucide-react";
import {
    getUnitCategoryConfiguration,
    isKnownUnitCategory,
} from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import type { Unit } from "verdi-common/Configurations/Units/UnitConfigurations";
import { UnitConfigurations } from "verdi-common/Configurations/Units/UnitConfigurations";
import { getUnitCategoryName } from "verdi-common/i18n";
import { Button } from "@/components/ui/button";
import {
    Menu,
    MenuGroup,
    MenuGroupLabel,
    MenuPopup,
    MenuRadioGroup,
    MenuRadioItem,
    MenuSeparator,
    MenuTrigger,
} from "@/components/ui/menu";
import { useSupportedLocale } from "@/hooks/use-locale";
import { cn } from "@/utils/class-name";
import { AxisSide, type AxisSpec } from "../../ChartSpec";
import { useDataViewerStore } from "../../data-viewer-provider";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import { UNIT_PICKER_CHEVRON_CLASS, UNIT_PICKER_CLASS } from "../unit-badge-tokens";

function findAxisForSide(side: AxisSide, axes: AxisSpec[], activeLeftAxisId: string | undefined) {
    if (side === AxisSide.Left) {
        return axes.find((candidate) => candidate.id === activeLeftAxisId) ?? axes.find((a) => a.side === side);
    }
    return axes.find((candidate) => candidate.side === side);
}

export function UnitBadge({
    side,
    axes,
    getDisplayInfo,
    activeLeftAxisId,
    leftAxes,
    onActiveLeftAxisChange,
}: {
    side: AxisSide;
    axes: AxisSpec[];
    getDisplayInfo: GetDisplayInfo;
    activeLeftAxisId: string | undefined;
    leftAxes: AxisSpec[];
    onActiveLeftAxisChange: (axisId: string) => void;
}) {
    const setUnitOverride = useDataViewerStore((state) => state.setUnitOverride);
    const locale = useSupportedLocale();

    const axis = findAxisForSide(side, axes, activeLeftAxisId);
    if (!axis) return null;

    const { unitCategory } = axis;
    const displayInfo = getDisplayInfo(unitCategory);
    const label = axis.label ?? (displayInfo.isKnown ? displayInfo.symbol : unitCategory);
    const allowedUnits = getUnitCategoryConfiguration(unitCategory)?.allowedUnits ?? [];

    const hasAxisPicker = side === AxisSide.Left && leftAxes.length > 1 && activeLeftAxisId != null;
    const hasUnitPicker = displayInfo.isKnown && allowedUnits.length > 1;

    // Dimensionless units have an empty symbol, which would draw an empty box.
    if (!label && !hasAxisPicker) return null;

    const isButtonDisabled = !hasAxisPicker && !hasUnitPicker;

    let tooltip: string | undefined;
    if (hasAxisPicker) {
        tooltip = "Choose y-axis";
    } else if (hasUnitPicker) {
        tooltip = "Change unit";
    }

    const badgeClassName = cn("leading-none", UNIT_PICKER_CLASS);

    // No pickers: same outline look as an interactive badge, but not a menu trigger (and not faded).
    // Disabled Button (full opacity) keeps keyboard/mouse from opening an empty Menu; prefer this over
    // pointer-events-none alone, which still left the control focusable.
    if (isButtonDisabled) {
        return (
            <Button
                size={"xs"}
                variant={"outline"}
                disabled
                className={cn(badgeClassName, "cursor-default disabled:opacity-100")}
            >
                {label}
            </Button>
        );
    }

    return (
        <Menu>
            <MenuTrigger
                render={
                    <Button
                        aria-label={`${tooltip}, currently ${label}`}
                        size={"xs"}
                        variant={"outline"}
                        className={badgeClassName}
                        tooltip={tooltip}
                    />
                }
            >
                {label}
                <ChevronDown className={UNIT_PICKER_CHEVRON_CLASS} strokeWidth={3} />
            </MenuTrigger>
            <MenuPopup side={"bottom"} align={"start"} sideOffset={2}>
                {hasAxisPicker ? (
                    <MenuGroup>
                        <MenuGroupLabel>Metric</MenuGroupLabel>
                        <MenuRadioGroup
                            value={activeLeftAxisId}
                            onValueChange={(axisId) => {
                                if (axisId) onActiveLeftAxisChange(axisId);
                            }}
                        >
                            {leftAxes.map((candidate) => (
                                <MenuRadioItem key={candidate.id} value={candidate.id} closeOnClick={false}>
                                    {getUnitCategoryName(candidate.unitCategory, locale)}
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                    </MenuGroup>
                ) : null}
                {hasAxisPicker && hasUnitPicker ? <MenuSeparator /> : null}
                {hasUnitPicker ? (
                    <MenuGroup>
                        {hasAxisPicker ? <MenuGroupLabel>Unit</MenuGroupLabel> : null}
                        <MenuRadioGroup
                            value={displayInfo.unit}
                            onValueChange={(unit) => {
                                if (unit !== null && isKnownUnitCategory(unitCategory)) {
                                    setUnitOverride(unitCategory, unit as Unit);
                                }
                            }}
                        >
                            {allowedUnits.map((unit) => (
                                <MenuRadioItem key={unit} value={unit} closeOnClick>
                                    {UnitConfigurations[unit]?.symbol ?? unit}
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                    </MenuGroup>
                ) : null}
            </MenuPopup>
        </Menu>
    );
}
